import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import {
  CompatibleSpatialMetricDetailsFragment,
  DraftReportDependenciesDocument,
  DraftReportOverlaySourcesDocument,
  ReportDependenciesDocument,
  ReportOverlaySourcesDocument,
  SpatialMetricState,
  useRecalculateSpatialMetricsMutation,
  useRetryFailedSpatialMetricsMutation,
} from "../../generated/graphql";
import { useGlobalErrorHandler } from "../../components/GlobalErrorHandler";
import ReportMetricsProgressDetails from "../ReportMetricsProgressDetails";
import { collectReportCardTitle } from "../../admin/sketchClasses/SketchClassReportsAdmin";
import { ReportCardConfiguration } from "../cards/cards";
import { subjectIsFragment } from "overlay-engine";
import { useCardDependenciesContext } from "../context/CardDependenciesContext";
import { useBaseReportContext } from "../context/BaseReportContext";
import { useSubjectReportContext } from "../context/SubjectReportContext";
import { evictSubjectReportCachesForSketchId } from "../utils/evictReportDependenciesCache";
import { DraftReportContext } from "../DraftReportContext";

export interface CalculationDetailsModalState {
  open: boolean;
  cardId: number | null;
}

export interface UseCalculationDetailsModalStateReturn {
  state: CalculationDetailsModalState;
  openModal: (cardId: number) => void;
  closeModal: () => void;
}

/**
 * Hook to manage the state of the CalculationDetailsModal.
 * Use this in ReportEditor to control when the modal opens and which card's details to show.
 */
export function useCalculationDetailsModalState(): UseCalculationDetailsModalStateReturn {
  const [state, setState] = useState<CalculationDetailsModalState>({
    open: false,
    cardId: null,
  });

  const openModal = useCallback((cardId: number) => {
    setState({ open: true, cardId });
  }, []);

  const closeModal = useCallback(() => {
    setState({ open: false, cardId: null });
  }, []);

  return { state, openModal, closeModal };
}

interface CalculationDetailsModalProps {
  state: CalculationDetailsModalState;
  onClose: () => void;
  /** The card configuration - looked up by ReportEditor based on cardId */
  config?: ReportCardConfiguration<any>;
  /** Metrics for this card */
  metrics?: CompatibleSpatialMetricDetailsFragment[];
  /** Report editor: full recalculate UX (nested modal, etc.) */
  adminMode?: boolean;
  /**
   * When set, controls download links / topology details in the modal body.
   * Defaults to adminMode. Use true for project admins viewing from the map or reports.
   */
  showAdminDetails?: boolean;
}

/**
 * Modal showing calculation/data source details for a report card.
 */
export function CalculationDetailsModal({
  state,
  onClose,
  config,
  adminMode = false,
  showAdminDetails,
}: CalculationDetailsModalProps) {
  const detailsAdmin = showAdminDetails ?? adminMode;
  const { t } = useTranslation("admin:sketching");
  const onError = useGlobalErrorHandler();
  const context = useCardDependenciesContext();
  const draftReportContext = useContext(DraftReportContext);
  const baseReport = useBaseReportContext();
  const subjectReport = useSubjectReportContext();
  const reportIdForCache = baseReport.report.id;
  const sketchIdForCache = subjectReport.data?.sketch?.id;

  // Recalculate modal state (nested modal)
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recomputePreprocessed, setRecomputePreprocessed] = useState(false);
  const [recomputeTotals, setRecomputeTotals] = useState(false);

  const [recalculate, recalculateState] = useRecalculateSpatialMetricsMutation({
    onError,
    update(cache) {
      if (sketchIdForCache != null) {
        evictSubjectReportCachesForSketchId(cache, sketchIdForCache, {
          reportId: reportIdForCache,
        });
      }
    },
  });

  const [retryFailedMetrics, retryState] = useRetryFailedSpatialMetricsMutation(
    {
      onError,
      update(cache) {
        if (sketchIdForCache != null) {
          evictSubjectReportCachesForSketchId(cache, sketchIdForCache, {
            reportId: reportIdForCache,
          });
        }
      },
      refetchQueries: [ReportDependenciesDocument],
      awaitRefetchQueries: true,
    }
  );

  // Compute loading and failed metrics
  const { loading, failedMetrics } = useMemo(() => {
    let loading = false;
    const failedMetrics: number[] = [];

    for (const metric of context.metrics) {
      if (metric.state === SpatialMetricState.Error) {
        failedMetrics.push(metric.id);
      } else if (metric.state !== SpatialMetricState.Complete) {
        loading = true;
      }
    }

    return { loading, failedMetrics };
  }, [context.metrics]);

  const handleRecalculate = useCallback(async () => {
    const metricsToRecalculate: number[] = [];
    const seenIds = new Set<number>();
    for (const metric of [
      ...draftReportContext.draftMetrics,
      ...context.metrics,
    ]) {
      if (metric.id != null && seenIds.has(metric.id)) continue;
      if (metric.id != null) seenIds.add(metric.id);
      if (subjectIsFragment(metric.subject) || recomputeTotals) {
        metricsToRecalculate.push(metric.id);
      }
    }

    await recalculate({
      variables: {
        metricIds: metricsToRecalculate,
        preprocessSources: recomputePreprocessed,
      },
      refetchQueries: [
        ReportDependenciesDocument,
        ReportOverlaySourcesDocument,
        DraftReportDependenciesDocument,
        DraftReportOverlaySourcesDocument,
      ],
      awaitRefetchQueries: true,
    });
    setRecalcOpen(false);
  }, [
    context.metrics,
    draftReportContext.draftMetrics,
    recomputeTotals,
    recomputePreprocessed,
    recalculate,
  ]);

  const handleRecalculateClick = useCallback(async () => {
    if (adminMode) {
      setRecalcOpen(true);
    } else {
      const metricsToRecalculate: number[] = [];
      for (const metric of context.metrics) {
        if (subjectIsFragment(metric.subject)) {
          metricsToRecalculate.push(metric.id);
        }
      }
      await recalculate({
        variables: {
          metricIds: metricsToRecalculate,
          preprocessSources: false,
        },
        refetchQueries: [ReportDependenciesDocument],
        awaitRefetchQueries: true,
      });
    }
  }, [adminMode, context.metrics, recalculate]);

  const handleRetryFailed = useCallback(async () => {
    await retryFailedMetrics({
      variables: {
        metricIds: failedMetrics,
      },
    });
  }, [retryFailedMetrics, failedMetrics]);

  const handleClose = useCallback(() => {
    if (!recalcOpen) {
      onClose();
    }
  }, [onClose, recalcOpen]);

  if (!state.open || !config) {
    return null;
  }

  const cardTitle = collectReportCardTitle(config.body);

  return (
    <Modal
      open
      scrollable
      onRequestClose={handleClose}
      title={
        <div>
          <span className="text-gray-500 block text-base">{cardTitle}</span>
          <span>{t("Data Sources and Calculations")}</span>
        </div>
      }
      disableBackdropClick={recalcOpen}
      footer={[
        {
          label: t("Close"),
          onClick: handleClose,
          variant: "secondary",
        },
        {
          label: t("Recalculate"),
          onClick: handleRecalculateClick,
          disabled: loading || recalculateState.loading,
          variant: "secondary",
          loading: recalculateState.loading,
        },
        ...(failedMetrics.length > 0
          ? [
              {
                label: t("Retry failed calculations"),
                onClick: handleRetryFailed,
                disabled: retryState.loading,
                variant: "danger" as const,
                loading: retryState.loading,
              },
            ]
          : []),
      ]}
    >
      <ReportMetricsProgressDetails config={config} isAdmin={detailsAdmin} />
      {recalcOpen && (
        <Modal
          open
          tipyTop
          onRequestClose={() => setRecalcOpen(false)}
          title={t("Recalculate results")}
          disableBackdropClick={false}
          footerClassName="bg-gray-100 border-t"
          footer={[
            {
              label: t("Cancel"),
              onClick: () => setRecalcOpen(false),
              variant: "secondary",
            },
            {
              label: t("Recalculate"),
              onClick: handleRecalculate,
              variant: "danger",
              loading: recalculateState.loading,
            },
          ]}
        >
          <div className="space-y-4 mb-4">
            <p className="text-sm">
              {t(
                "This will recompute all metrics for this card. Depending on data size, this may take some time. If you choose to recreate optimized layers, this will delete the cache of any related metrics."
              )}
            </p>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded outline-none ring-0 active:ring-0 focus:ring-0 focus-visible:ring-2"
                checked={recomputePreprocessed}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRecomputePreprocessed(checked);
                  if (checked) {
                    setRecomputeTotals(true);
                  }
                }}
              />
              <span>{t("Recreate optimized layers")}</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded outline-none ring-0 active:ring-0 focus:ring-0 focus-visible:ring-2 disabled:opacity-50"
                checked={recomputeTotals}
                onChange={(e) => setRecomputeTotals(e.target.checked)}
                disabled={recomputePreprocessed}
              />
              <span>{t("Recalculate geography totals (slow)")}</span>
            </label>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
